import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Button, FoodsIcon } from "@foodpilot/foods";
import {
    ConsentGovernanceItem,
    useGetAdminConsentItems,
} from "src/api/client-api/consent/useGetConsentGovernanceAdmin";
import {
    getConsentGovernanceErrorMessage,
    useUpsertAdminConsentItem,
} from "src/api/client-api/consent/useManageConsentGovernanceAdmin";
import { type Locale } from "src/api/locales/useLocales";
import { useSnackbar } from "src/utils/useSnackbar";
import { ConsentItemGlobalFields, ConsentItemLocalizedFields } from "./components/ConsentItemModalFields";
import {
    buildConsentItemPayload,
    canSaveConsentItemLocalizedDraft,
    ConsentItemFormSchema,
    type ConsentItemFormData,
    type ConsentItemLocalizedDraft,
    getConsentItemGlobalSyncKey,
} from "./utils/consentItemModalModel";

type ConsentItemModalProps = {
    consentItem?: ConsentGovernanceItem;
    itemType: "document" | "opt_in";
    locale: string;
    localeOptions?: Array<Locale & { code: string }>;
    onClose: () => void;
    onSuccess?: (item: ConsentGovernanceItem) => void;
    open: boolean;
};

export const ConsentItemModal = (props: ConsentItemModalProps) => {
    const { consentItem, itemType, locale, localeOptions = [], onClose, onSuccess, open } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const mutation = useUpsertAdminConsentItem();
    const [editingLocale, setEditingLocale] = useState(locale);
    const [globalContentSyncKey, setGlobalContentSyncKey] = useState<string | null>(null);
    const [localizedDrafts, setLocalizedDrafts] = useState<Record<string, ConsentItemLocalizedDraft>>({});
    const defaultLocaleCode = useMemo(
        () => localeOptions.find((option) => option.isDefault)?.code ?? locale,
        [locale, localeOptions],
    );
    const isDefaultEditingLocale = editingLocale === defaultLocaleCode;
    const { consentItems: localizedConsentItems = [], isFetching: isLocalizedConsentItemsFetching } =
        useGetAdminConsentItems({
            enabled: open && consentItem !== undefined,
            itemsPerPage: 200,
            locale: editingLocale,
            queryParams: {
                code: consentItem?.code,
                "order[id]": "asc",
            },
        });
    const { consentItems: translatedNameConsentItems = [], isFetching: isTranslatedNameConsentItemsFetching } =
        useGetAdminConsentItems({
            enabled: open && consentItem !== undefined && !isDefaultEditingLocale,
            itemsPerPage: 200,
            locale: editingLocale,
            queryParams: {
                code: consentItem?.code,
                contentLocale: editingLocale,
                contentLocaleFields: "name",
            },
        });
    const {
        consentItems: translatedDescriptionConsentItems = [],
        isFetching: isTranslatedDescriptionConsentItemsFetching,
    } = useGetAdminConsentItems({
        enabled: open && consentItem !== undefined && !isDefaultEditingLocale,
        itemsPerPage: 200,
        locale: editingLocale,
        queryParams: {
            code: consentItem?.code,
            contentLocale: editingLocale,
            contentLocaleFields: "description",
        },
    });
    const translatedNameConsentItem = useMemo(
        () => translatedNameConsentItems.find((item) => item.id === consentItem?.id),
        [consentItem?.id, translatedNameConsentItems],
    );
    const translatedDescriptionConsentItem = useMemo(
        () => translatedDescriptionConsentItems.find((item) => item.id === consentItem?.id),
        [consentItem?.id, translatedDescriptionConsentItems],
    );
    const localizedConsentItem = useMemo(
        () => localizedConsentItems.find((item) => item.id === consentItem?.id),
        [consentItem?.id, localizedConsentItems],
    );
    const hasLocalizedName =
        consentItem === undefined || isDefaultEditingLocale || translatedNameConsentItem !== undefined;
    const hasLocalizedDescription =
        consentItem === undefined || isDefaultEditingLocale || translatedDescriptionConsentItem !== undefined;
    const hasAnyLocalizedConsentItemField = hasLocalizedName || hasLocalizedDescription;
    const localizedName = consentItem && hasLocalizedName ? (localizedConsentItem?.name ?? "") : "";
    const localizedDescription =
        consentItem && hasLocalizedDescription ? (localizedConsentItem?.description ?? "") : "";

    const {
        control,
        getValues,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isValid },
    } = useForm<ConsentItemFormData>({
        mode: "onChange",
        resolver: zodResolver(ConsentItemFormSchema),
        defaultValues: {
            code: "",
            name: "",
            description: "",
            category: "",
            sortOrder: 0,
            active: true,
        },
    });

    const isLocalizedContentFetching =
        isLocalizedConsentItemsFetching ||
        isTranslatedNameConsentItemsFetching ||
        isTranslatedDescriptionConsentItemsFetching;
    const activeLocalizedDraft = localizedDrafts[editingLocale];
    const isActiveLocalizedDraftSynchronized =
        activeLocalizedDraft?.initialized === true &&
        activeLocalizedDraft.name === localizedName &&
        activeLocalizedDraft.description === localizedDescription;
    const isLocalizedContentLoading =
        consentItem !== undefined &&
        !activeLocalizedDraft?.dirty &&
        (isLocalizedContentFetching || !isActiveLocalizedDraftSynchronized);
    const formName =
        activeLocalizedDraft?.initialized ? activeLocalizedDraft.name
        : isLocalizedContentFetching ? ""
        : localizedName;
    const formDescription =
        activeLocalizedDraft?.initialized ? activeLocalizedDraft.description
        : isLocalizedContentFetching ? ""
        : localizedDescription;
    const watchedName = watch("name") ?? "";
    const watchedDescription = watch("description") ?? "";
    const getPayloadFallbackName = useCallback(
        (draftLocale: string) => (draftLocale === defaultLocaleCode ? undefined : consentItem?.name),
        [consentItem?.name, defaultLocaleCode],
    );
    const hasSaveableLocalizedDraft = useMemo(() => {
        const draftsToEvaluate = {
            ...localizedDrafts,
            [editingLocale]: {
                description: watchedDescription,
                dirty: true,
                initialized: true,
                name: watchedName,
            },
        };

        return Object.entries(draftsToEvaluate).some(
            ([draftLocale, draft]) =>
                (draftLocale === editingLocale || draft.dirty) &&
                canSaveConsentItemLocalizedDraft(draft, getPayloadFallbackName(draftLocale)),
        );
    }, [editingLocale, getPayloadFallbackName, localizedDrafts, watchedDescription, watchedName]);
    const consentItemGlobalSyncKey = getConsentItemGlobalSyncKey(consentItem, itemType);
    const isGlobalContentLoading = consentItem !== undefined && globalContentSyncKey !== consentItemGlobalSyncKey;

    useEffect(() => {
        if (!open) {
            return;
        }

        setEditingLocale(locale);
        setLocalizedDrafts({});
    }, [locale, open]);

    useEffect(() => {
        if (!open || isLocalizedContentFetching || activeLocalizedDraft?.dirty) {
            return;
        }

        setLocalizedDrafts((previousDrafts) => {
            const currentDraft = previousDrafts[editingLocale];

            if (currentDraft?.dirty) {
                return previousDrafts;
            }

            if (
                currentDraft?.initialized &&
                currentDraft.name === localizedName &&
                currentDraft.description === localizedDescription
            ) {
                return previousDrafts;
            }

            return {
                ...previousDrafts,
                [editingLocale]: {
                    description: localizedDescription,
                    dirty: false,
                    initialized: true,
                    name: localizedName,
                },
            };
        });
    }, [editingLocale, isLocalizedContentFetching, localizedDescription, localizedName, open]);

    useEffect(() => {
        if (!open || isLocalizedContentLoading) {
            return;
        }

        reset({
            code: consentItem?.code ?? "",
            name: formName,
            description: formDescription,
            category: consentItem?.category ?? "",
            sortOrder: consentItem?.sortOrder ?? 0,
            active: consentItem?.active ?? true,
        });
        setGlobalContentSyncKey(consentItemGlobalSyncKey);
    }, [
        activeLocalizedDraft?.dirty,
        consentItem,
        consentItemGlobalSyncKey,
        formDescription,
        formName,
        isLocalizedContentLoading,
        open,
        reset,
    ]);

    const updateLocalizedDraft = useCallback(
        (patch: Partial<Pick<ConsentItemLocalizedDraft, "description" | "name">>) => {
            setLocalizedDrafts((previousDrafts) => {
                const currentDraft = previousDrafts[editingLocale] ?? {
                    description: getValues("description") ?? "",
                    dirty: false,
                    initialized: true,
                    name: getValues("name") ?? "",
                };

                return {
                    ...previousDrafts,
                    [editingLocale]: {
                        ...currentDraft,
                        ...patch,
                        dirty: true,
                        initialized: true,
                    },
                };
            });
        },
        [editingLocale, getValues],
    );

    const persistCurrentLocalizedDraft = useCallback(() => {
        const values = getValues();

        setLocalizedDrafts((previousDrafts) => ({
            ...previousDrafts,
            [editingLocale]: {
                description: values.description ?? "",
                dirty:
                    previousDrafts[editingLocale]?.dirty ||
                    previousDrafts[editingLocale]?.description !== (values.description ?? "") ||
                    previousDrafts[editingLocale]?.name !== (values.name ?? ""),
                initialized: true,
                name: values.name ?? "",
            },
        }));
    }, [editingLocale, getValues]);

    const handleEditingLocaleChange = (nextLocale: string) => {
        const values = getValues();
        const nextDraft = localizedDrafts[nextLocale];

        persistCurrentLocalizedDraft();
        setEditingLocale(nextLocale);
        reset({
            ...values,
            name: nextDraft?.initialized ? nextDraft.name : "",
            description: nextDraft?.initialized ? nextDraft.description : "",
        });
    };

    const handleClose = () => {
        reset();
        setGlobalContentSyncKey(null);
        setLocalizedDrafts({});
        onClose();
    };

    const onSubmitHandler: SubmitHandler<ConsentItemFormData> = async (values) => {
        const draftsToSave = {
            ...localizedDrafts,
            [editingLocale]: {
                description: values.description ?? "",
                dirty: true,
                initialized: true,
                name: values.name,
            },
        };
        const localizedEntries = Object.entries(draftsToSave).filter(
            ([draftLocale, draft]) =>
                (draftLocale === editingLocale || draft.dirty) &&
                canSaveConsentItemLocalizedDraft(draft, getPayloadFallbackName(draftLocale)),
        );

        try {
            let savedItem: ConsentGovernanceItem | undefined;
            let savedItemId = consentItem?.id;

            for (const [draftLocale, draft] of localizedEntries) {
                savedItem = await mutation.mutateAsync({
                    id: savedItemId,
                    data: buildConsentItemPayload(values, draft, itemType, getPayloadFallbackName(draftLocale)),
                    locale: draftLocale,
                });
                savedItemId ??= savedItem.id;
            }

            if (savedItem) {
                snackbar.setSnackbarProps({
                    message:
                        consentItem ?
                            t("settings.consent.item.updated", "Consent item updated successfully")
                        :   t("settings.consent.item.created", "Consent item created successfully"),
                    type: "success",
                });
                onSuccess?.(savedItem);
                handleClose();
            }
        } catch (error) {
            const fallbackMessage = t("settings.consent.item.error", "Failed to save the consent item.");

            snackbar.setSnackbarProps({
                message: getConsentGovernanceErrorMessage(error, t, fallbackMessage),
                type: "error",
            });
        }
    };

    const title =
        consentItem ?
            itemType === "document" ?
                t("settings.consent.item.edit_document", "Edit document")
            :   t("settings.consent.item.edit_preference", "Edit preference")
        : itemType === "document" ? t("settings.consent.item.add_document", "Add document")
        : t("settings.consent.item.add_preference", "Add preference");

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: (theme) => theme.spacing(2),
                },
            }}
        >
            <DialogTitle sx={{ px: 4, py: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h3" sx={{ fontSize: "22px", fontWeight: 700 }}>
                        {title}
                    </Typography>
                    <IconButton onClick={handleClose}>
                        <FoodsIcon icon="close" size={1.5} />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ px: 4, pb: 3 }}>
                <Stack component="form" gap={3} onSubmit={handleSubmit(onSubmitHandler)}>
                    <ConsentItemGlobalFields
                        control={control}
                        errors={errors}
                        isGlobalContentLoading={isGlobalContentLoading}
                        itemType={itemType}
                    />
                    <ConsentItemLocalizedFields
                        consentItemId={consentItem?.id}
                        control={control}
                        editingLocale={editingLocale}
                        errors={errors}
                        hasAnyLocalizedConsentItemField={hasAnyLocalizedConsentItemField}
                        isLocalizedContentFetching={isLocalizedContentFetching}
                        isLocalizedContentLoading={isLocalizedContentLoading}
                        itemType={itemType}
                        localeOptions={localeOptions}
                        mutationIsLoading={mutation.isLoading}
                        open={open}
                        onEditingLocaleChange={handleEditingLocaleChange}
                        onLocalizedDraftChange={updateLocalizedDraft}
                    />
                    <DialogActions sx={{ px: 0, pb: 0 }}>
                        <Button variant="secondary" onClick={handleClose}>
                            {t("cancel", "Cancel")}
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={
                                !isValid ||
                                !hasSaveableLocalizedDraft ||
                                mutation.isLoading ||
                                isGlobalContentLoading ||
                                isLocalizedContentLoading
                            }
                        >
                            {mutation.isLoading ? t("common.saving", "Saving...") : t("common.save", "Save")}
                        </Button>
                    </DialogActions>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};
